// [SIDEBAR-PREMIUM] - Sidebar redesign niveau SaaS $1M
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import ValidationPendingScreen from "./ValidationPendingScreen";
import { UtensilsCrossed } from "lucide-react";
import "./layout/LayoutStyles.css";
import { 
  LayoutGrid, 
  Package, 
  Tags, 
  LogOut, 
  Menu, 
  X,
  Barcode,
  ShoppingCart,
  Settings,
  History,
  MessageSquare,
  Star,
  Wifi,
  WifiOff,
  Users,
  Bell,
  Lock,
  Crown,
  ShoppingBag,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Link2,
  Copy,
  Check,
  UsersRound,
  Calculator,
  Shirt,
  Box,
  ClipboardList,
  Clock,
  Sun,
  Moon,
  MessageCircle,
  ScrollText,
  Ban
} from "lucide-react";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { getFeatures } from "../lib/boutiqueType";
import { applySeo } from "../lib/seo";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL;

// [SIDEBAR-PREMIUM] Navigation items avec groupes et restrictions par rôle
const navGroups = [
  {
    label: "PRINCIPAL",
    items: [
      { path: "/admin/dashboard", icon: LayoutGrid, label: "Tableau de bord", badge: "livesales", roles: ['owner', 'comptable'] },
      { path: "/admin/sales", icon: Calculator, label: "Caisse", roles: ['owner', 'vendeur'] },
      { path: "/admin/tables", icon: UtensilsCrossed, label: "Tables", roles: ['owner', 'vendeur', 'serveur'], feature: "tableOrders" },
      { path: "/admin/service", icon: ClipboardList, label: "Prise de commande", roles: ['owner', 'vendeur', 'serveur'], feature: "tableOrders" },
    ]
  },
  {
    label: "CATALOGUE",
    items: [
      { path: "/admin/products", icon: Package, label: "Articles", roles: ['owner', 'vendeur'] },
      { path: "/admin/categories", icon: Tags, label: "Collections", roles: ['owner', 'vendeur'], labelKey: "categoryLabelPlural" },
      { path: "/admin/labels", icon: Barcode, label: "Étiquettes", roles: ['owner', 'vendeur'], feature: "labels" },
    ]
  },
  {
    label: "CLIENTS",
    items: [
      { path: "/admin/customers", icon: Users, label: "Clientes", roles: ['owner'], feature: "customers" },
      { path: "/admin/orders", icon: ClipboardList, label: "Commandes", badge: "orders", roles: ['owner'], feature: "orders" },
      // [CONV-1-SYSTEME] Messages -> Conversations page
      { path: "/admin/conversations", icon: MessageCircle, label: "Messages", badge: "messages", roles: ['owner'], feature: "chat" },
      // { path: "/admin/reviews", icon: Star, label: "Avis clients", badge: "reviews", roles: ['owner'] }, // Masqué temporairement
    ]
  },
  {
    label: "GESTION",
    items: [
      { path: "/admin/history", icon: History, label: "Historique", roles: ['owner', 'comptable'] },
      { path: "/admin/activity", icon: ScrollText, label: "Journal", roles: ['owner', 'comptable'] },
      { path: "/admin/cancellations", icon: Ban, label: "Annulations", roles: ['owner', 'comptable'], feature: "tableOrders" },
      { path: "/admin/team", icon: UsersRound, label: "Équipe", roles: ['owner'] },
      { path: "/admin/settings", icon: Settings, label: "Paramètres", roles: ['owner'] },
    ]
  }
];

// [SIDEBAR-PREMIUM] Flatten pour la logique d'autorisation
const navItems = navGroups.flatMap(g => g.items);

// [SIDEBAR-PREMIUM] Configuration des couleurs
const COLORS = {
  // Fonds
  sidebarBg: '#111111',
  cardBg: '#1A1A1A',
  appBg: '#0A0A0A',
  hoverBg: 'rgba(255,255,255,0.05)',
  separator: 'rgba(255,255,255,0.06)',
  // Accent
  accent: '#FF4500',
  accentDim: 'rgba(255,69,0,0.12)',
  accentBorder: 'rgba(255,69,0,0.25)',
  linkBg: 'rgba(255,69,0,0.08)',
  linkBorder: 'rgba(255,69,0,0.20)',
  // Textes
  textPrimary: '#FFFFFF',
  textSecondary: '#9CA3AF',
  textHint: '#6B7280',
  textGroupLabel: '#4B5563',
  border: '#2A2A2A',
  // Badges
  badgeRed: '#EF4444',
  badgeOrange: '#F59E0B',
  badgeGreen: '#22C55E',
  // Topbar
  topbarBg: '#111111',
  buttonBg: '#1A1A1A',
  buttonBorder: '#2A2A2A',
};

// [SIDEBAR-PREMIUM] Configuration des rôles
const roleConfig = {
  owner: { label: "Propriétaire", icon: Crown, gradient: `linear-gradient(135deg, ${COLORS.accent} 0%, #CC3700 100%)`, color: COLORS.accent },
  vendeur: { label: "Vendeur", icon: ShoppingBag, gradient: "linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)", color: "#60A5FA" },
  comptable: { label: "Comptable", icon: BarChart3, gradient: "linear-gradient(135deg, #10B981 0%, #047857 100%)", color: "#34D399" },
  serveur: { label: "Serveur", icon: UtensilsCrossed, gradient: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)", color: "#A78BFA" }
};

// [SIDEBAR-PREMIUM] Utilitaires
const getInitials = (name) => {
  if (!name) return "B";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const getBoutiqueColor = (boutiqueData) => {
  if (boutiqueData?.customColor) return boutiqueData.customColor;
  const palettes = {
    luxe: "#D4AF37", nude: "#E8B4B8", emerald: "#1A7A4A",
    bordeaux: "#8B1A1A", ocean: "#1A5A7A", lavande: "#9B7AB8",
    terracotta: "#C4725F", forest: "#2D5A3D", minimal: "#1A1A1A"
  };
  return palettes[boutiqueData?.colorPalette] || "#D4AF37";
};

// [SIDEBAR-PREMIUM] Composant Tooltip
const Tooltip = ({ children, content, side = "right" }) => {
  const [show, setShow] = useState(false);
  
  return (
    <div 
      className="relative"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div 
          className="tooltip-content"
          style={{
            position: 'absolute',
            [side]: side === 'right' ? '100%' : undefined,
            left: side === 'top' ? '50%' : (side === 'right' ? undefined : 0),
            top: side === 'top' ? undefined : '50%',
            bottom: side === 'top' ? '100%' : undefined,
            transform: side === 'top' ? 'translateX(-50%)' : 'translateY(-50%)',
            marginLeft: side === 'right' ? '12px' : 0,
            marginBottom: side === 'top' ? '8px' : 0,
            background: 'var(--sidebar-tooltip-bg)',
            color: 'var(--sidebar-tooltip-text)',
            fontSize: '13px',
            fontWeight: 500,
            padding: '6px 12px',
            borderRadius: '8px',
            whiteSpace: 'nowrap',
            zIndex: 100,
            opacity: 1,
            animation: 'tooltipFadeIn 150ms ease-out'
          }}
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default function Layout() {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // [SIDEBAR-PREMIUM] États
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [ordersCount, setOrdersCount] = useState(0);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [messagesCount, setMessagesCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [boutiqueData, setBoutiqueData] = useState(null);
  const [sidebarLogo, setSidebarLogo] = useState("");
  const [sidebarProfilePhoto, setSidebarProfilePhoto] = useState("");
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('owner');
  const [activeIndicatorTop, setActiveIndicatorTop] = useState(0);
  // [LIVE-VENTES] Badge compteur ventes non-vues
  const [liveSalesBadge, setLiveSalesBadge] = useState(0);
  const liveSalesRef = useRef(null);
  
  // [THEME] Global theme state — loaded from MongoDB, fallback localStorage
  const [isDark, setIsDark] = useState(() => localStorage.getItem("dashboardTheme") !== "light");
  const toggleTheme = useCallback(() => {
    setIsDark(prev => {
      const next = !prev;
      const theme = next ? "dark" : "light";
      localStorage.setItem("dashboardTheme", theme);
      // Save to MongoDB
      axios.put(`${API}/api/settings/theme`, { theme }, getAuthHeaders()).catch(() => {});
      return next;
    });
  }, []);

  // [SEO] Espace admin : jamais indexé
  useEffect(() => {
    applySeo({ title: "BoutiqueOS — Administration", noindex: true, jsonLdId: "vitrine", jsonLd: null });
  }, []);

  // [THEME] Propagate theme globally to <html> so portals (dialogs, dropdowns, toasts) inherit it
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("dark");
      root.setAttribute("data-theme", "dark");
    } else {
      root.classList.remove("dark");
      root.setAttribute("data-theme", "light");
    }
    root.style.colorScheme = isDark ? "dark" : "light";
  }, [isDark]);
  // [VALIDATION-FIX] Lire compteValide depuis sessionStorage (source: MongoDB via login)
  const [compteValide, setCompteValide] = useState(() => {
    return sessionStorage.getItem('compteValide') !== 'false';
  });

  // [SIDEBAR-PREMIUM] Charger les données
  useEffect(() => {
    try {
      const storedBoutique = localStorage.getItem("boutique");
      if (storedBoutique) {
        const parsed = JSON.parse(storedBoutique);
        setBoutiqueData(parsed);
      }
      
      // [VALIDATION-FIX] compteValide vient de sessionStorage (source: MongoDB via login/register)
      const cv = sessionStorage.getItem('compteValide');
      setCompteValide(cv !== 'false');
      
      const sessionUser = sessionStorage.getItem("currentUser");
      if (sessionUser) {
        const parsed = JSON.parse(sessionUser);
        setCurrentUser(parsed);
        setUserRole(parsed.role || 'owner');
      } else {
        const storedRole = localStorage.getItem("userRole");
        if (storedRole) setUserRole(storedRole);
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
    
    // [THEME] Load theme preference from MongoDB
    axios.get(`${API}/api/settings/theme`, getAuthHeaders())
      .then(res => {
        const theme = res.data.theme || "dark";
        localStorage.setItem("dashboardTheme", theme);
        setIsDark(theme === "dark");
      })
      .catch(() => {});

    // [SETTINGS-SYNC] Load sidebar images (logo + profile photo)
    axios.get(`${API}/api/settings/profile`, getAuthHeaders())
      .then(res => {
        if (res.data.logo_url) setSidebarLogo(res.data.logo_url);
        if (res.data.profile_photo) setSidebarProfilePhoto(res.data.profile_photo);
      })
      .catch(() => {});
  }, []);

  // [SIDEBAR-PREMIUM] Online/Offline
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // [VALIDATION-FIX] Auto-redirection vers la caisse si compte non validé
  useEffect(() => {
    if (!compteValide && location.pathname !== '/admin/sales') {
      navigate('/admin/sales', { replace: true });
    }
  }, [compteValide, location.pathname, navigate]);

  // [SIDEBAR-PREMIUM] Fetch counts
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [ordersRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/api/orders/stats`, getAuthHeaders()),
          axios.get(`${API}/api/reviews/stats`, getAuthHeaders())
        ]);
        setOrdersCount(ordersRes.data.new || 0);
        setReviewsCount(reviewsRes.data.pending || 0);
        // [CONV-1-SYSTEME] Fetch conversations unread count
        try {
          const convRes = await axios.get(`${API}/api/conversations/unread/count`, getAuthHeaders());
          setMessagesCount(convRes.data.unread || 0);
        } catch { setMessagesCount(0); }
      } catch (error) {}
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // [LIVE-VENTES] Polling pour le badge sidebar (owner uniquement, hors dashboard)
  useEffect(() => {
    if (userRole !== 'owner') return;
    const lastCheck = { current: new Date().toISOString() };
    const interval = setInterval(async () => {
      // Si on est sur le dashboard, reset le badge
      if (location.pathname === '/admin/dashboard') {
        setLiveSalesBadge(0);
        lastCheck.current = new Date().toISOString();
        return;
      }
      try {
        const res = await axios.get(
          `${API}/api/live-sales?since=${encodeURIComponent(lastCheck.current)}`,
          getAuthHeaders()
        );
        const count = res.data?.nouvelles?.length || 0;
        if (count > 0) {
          setLiveSalesBadge(prev => prev + count);
          lastCheck.current = new Date().toISOString();
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [userRole, location.pathname]);

  // [LIVE-VENTES] Reset badge quand on arrive sur le dashboard
  useEffect(() => {
    if (location.pathname === '/admin/dashboard') {
      setLiveSalesBadge(0);
    }
  }, [location.pathname]);

  const getBadgeCount = (badgeType) => {
    if (badgeType === 'orders') return ordersCount;
    if (badgeType === 'reviews') return reviewsCount;
    if (badgeType === 'messages') return messagesCount;
    // [LIVE-VENTES] Badge pour le dashboard
    if (badgeType === 'livesales') return liveSalesBadge;
    return 0;
  };

  // [SIDEBAR-PREMIUM] Filtrer selon le rôle + profil boutique (caisse / mode)
  const features = useMemo(() => getFeatures(), []);
  const itemAllowed = useCallback((item) => {
    if (item.roles && !item.roles.includes(userRole)) return false;
    if (item.feature && !features[item.feature]) return false;
    return true;
  }, [userRole, features]);

  const filteredNavGroups = useMemo(() => {
    return navGroups.map(group => ({
      ...group,
      items: group.items
        .filter(itemAllowed)
        .map(item => item.labelKey ? { ...item, label: features[item.labelKey] } : item)
    })).filter(group => group.items.length > 0);
  }, [itemAllowed, features]);

  const filteredNavItems = useMemo(() => {
    return navItems.filter(itemAllowed);
  }, [itemAllowed]);

  // [VALIDATION-COMPTE] Chemins autorisés quand le compte n'est pas validé
  const ALLOWED_PATHS_UNVALIDATED = ['/admin/sales'];
  
  const isPathLocked = useCallback((path) => {
    if (compteValide) return false;
    return !ALLOWED_PATHS_UNVALIDATED.includes(path);
  }, [compteValide]);

  // [SIDEBAR-PREMIUM] Vérifier autorisation route (rôle + profil)
  const isRouteAuthorized = useMemo(() => {
    const currentPath = location.pathname;
    const matchingItem = navItems.find(item => currentPath.startsWith(item.path));
    if (!matchingItem) return true;
    return itemAllowed(matchingItem);
  }, [location.pathname, itemAllowed]);

  // [VALIDATION-COMPTE] Vérifier si la route actuelle est verrouillée
  const isCurrentRouteLocked = useMemo(() => {
    return isPathLocked(location.pathname);
  }, [location.pathname, isPathLocked]);

  const getDefaultRoute = () => {
    if (filteredNavItems.length > 0) return filteredNavItems[0].path;
    return "/admin/dashboard";
  };

  // [SIDEBAR-PREMIUM] Handlers
  const handleLogout = () => setShowLogoutDialog(true);
  
  const confirmLogout = () => {
    sessionStorage.removeItem("currentUser");
    localStorage.removeItem("userRole");
    localStorage.removeItem("currentTeamMember");
    logout();
    navigate("/");
  };

  const copyBoutiqueLink = () => {
    const link = `${window.location.origin}/vitrine/${user?.id}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    toast.success("Lien copié !");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // [SIDEBAR-PREMIUM] Données d'affichage
  const displayName = currentUser?.prenom || user?.name?.split(' ')[0] || "Admin";
  const roleInfo = roleConfig[userRole] || roleConfig.owner;
  const boutiqueName = boutiqueData?.nom || user?.name || "Ma Boutique";
  const boutiqueLogo = sidebarLogo || boutiqueData?.logo;
  const boutiquePlan = boutiqueData?.plan || "starter";

  // [SETTINGS-SYNC] Callback for Settings page to update sidebar images
  const updateSidebarImage = (type, url) => {
    if (type === "logo") setSidebarLogo(url);
    else if (type === "photo") setSidebarProfilePhoto(url);
  };

  return (
    <>
      <div className="premium-layout">
        {/* [SIDEBAR-PREMIUM] Desktop Sidebar */}
        <aside 
          className={`premium-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}
          data-testid="premium-sidebar"
        >
          {/* Glass effect overlay */}
          <div className="sidebar-glass" />
          
          {/* Toggle button */}
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>

          {/* Section 1: Header Boutique */}
          <div 
            className="sidebar-header"
            onClick={() => navigate('/admin/settings')}
          >
            {/* Logo/Avatar */}
            <div className="boutique-avatar" style={{ background: boutiqueLogo ? 'transparent' : `linear-gradient(135deg, ${COLORS.accent} 0%, #CC3700 100%)` }}>
              {boutiqueLogo ? (
                <img src={boutiqueLogo} alt={boutiqueName} />
              ) : (
                <span>{getInitials(boutiqueName)}</span>
              )}
            </div>
            
            {/* Text content */}
            <div className={`boutique-info ${sidebarCollapsed ? 'hidden' : ''}`}>
              <span className="boutique-name">{boutiqueName}</span>
              <span className="boutique-plan">
                {boutiquePlan.toUpperCase()}
              </span>
            </div>
            
            {!sidebarCollapsed && <ChevronDown className="header-chevron" size={14} />}
          </div>

          {/* Section 2: Navigation */}
          <nav className="sidebar-nav">
            {filteredNavGroups.map((group, groupIndex) => (
              <div key={group.label} className="nav-group">
                {/* Label groupe */}
                {!sidebarCollapsed ? (
                  <span 
                    className="nav-group-label"
                    style={{ animationDelay: `${groupIndex * 50}ms` }}
                  >
                    {group.label}
                  </span>
                ) : (
                  groupIndex > 0 && <div className="nav-group-divider" />
                )}
                
                {/* Items */}
                {group.items.map((item) => {
                  const badgeCount = item.badge ? getBadgeCount(item.badge) : 0;
                  const isActive = location.pathname.startsWith(item.path);
                  const locked = isPathLocked(item.path);
                  
                  const linkContent = locked ? (
                    <div
                      className="nav-item locked"
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}-locked`}
                    >
                      <item.icon className="nav-icon locked" />
                      <span className={`nav-label ${sidebarCollapsed ? 'hidden' : ''}`}>{item.label}</span>
                      {!sidebarCollapsed && <Lock className="nav-lock-icon" size={14} />}
                    </div>
                  ) : (
                    <NavLink
                      to={item.path}
                      className={`nav-item ${isActive ? 'active' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {/* Active indicator */}
                      {isActive && <div className="nav-active-indicator" />}
                      
                      {/* Icon */}
                      <item.icon className={`nav-icon ${isActive ? 'active' : ''}`} />
                      
                      {/* Label */}
                      <span className={`nav-label ${sidebarCollapsed ? 'hidden' : ''}`}>{item.label}</span>
                      
                      {/* Badge */}
                      {badgeCount > 0 && !sidebarCollapsed && (
                        <span className={`nav-badge ${item.badge === 'orders' ? 'orange' : 'red'}`}>
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      )}
                      
                      {/* Collapsed badge dot */}
                      {badgeCount > 0 && sidebarCollapsed && (
                        <span className={`nav-badge-dot ${item.badge === 'orders' ? 'orange' : 'red'}`} />
                      )}
                    </NavLink>
                  );
                  
                  return sidebarCollapsed ? (
                    <Tooltip key={item.path} content={item.label}>
                      {linkContent}
                    </Tooltip>
                  ) : (
                    <div key={item.path}>{linkContent}</div>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Section 3: Lien boutique rapide (vitrine — profil mode uniquement) */}
          {features.vitrine && (
          <div className={`sidebar-quick-link ${sidebarCollapsed ? 'collapsed' : ''}`}>
            {sidebarCollapsed ? (
              <Tooltip content="Copier lien boutique">
                <button className="quick-link-btn-collapsed" onClick={copyBoutiqueLink}>
                  <Link2 size={18} />
                </button>
              </Tooltip>
            ) : (
              <div className="quick-link-content">
                <div className="quick-link-info">
                  <Link2 size={16} className="quick-link-icon" />
                  <div className="quick-link-text">
                    <span>Votre boutique</span>
                    <span className="quick-link-sub">Copier le lien</span>
                  </div>
                </div>
                <button className="quick-link-copy" onClick={copyBoutiqueLink}>
                  {linkCopied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
            )}
          </div>
          )}

          {/* Offline indicator */}
          {!isOnline && !sidebarCollapsed && (
            <div className="sidebar-offline">
              <WifiOff size={14} />
              <span>Mode hors ligne</span>
            </div>
          )}

          {/* Section 4: Profil utilisateur */}
          <div className="sidebar-profile">
            {sidebarCollapsed ? (
              <Tooltip content={`${displayName} • ${roleInfo.label}`}>
                <div className="profile-avatar-wrapper" onClick={() => userRole !== 'owner' ? navigate('/admin/mon-profil') : null} style={{ cursor: userRole !== 'owner' ? 'pointer' : 'default' }}>
                  <div className="profile-avatar" style={{ background: sidebarProfilePhoto ? 'transparent' : roleInfo.gradient }}>
                    {sidebarProfilePhoto ? <img src={sidebarProfilePhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : displayName.charAt(0).toUpperCase()}
                  </div>
                </div>
              </Tooltip>
            ) : (
              <>
                <div className="profile-avatar" style={{ background: sidebarProfilePhoto ? 'transparent' : roleInfo.gradient, cursor: userRole !== 'owner' ? 'pointer' : 'default' }} onClick={() => userRole !== 'owner' ? navigate('/admin/mon-profil') : null}>
                  {sidebarProfilePhoto ? <img src={sidebarProfilePhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : displayName.charAt(0).toUpperCase()}
                </div>
                <div className="profile-info">
                  <span className="profile-name">{displayName}</span>
                  <span className="profile-role" style={{ color: roleInfo.color }}>{roleInfo.label}</span>
                </div>
                {userRole !== 'owner' && (
                  <Tooltip content="Mon profil" side="top">
                    <button className="profile-settings-btn" onClick={() => navigate('/admin/mon-profil')} data-testid="vendeur-settings-btn">
                      <Settings size={16} />
                    </button>
                  </Tooltip>
                )}
                <Tooltip content="Se déconnecter" side="top">
                  <button className="profile-logout" onClick={handleLogout} data-testid="logout-btn">
                    <LogOut size={18} />
                  </button>
                </Tooltip>
              </>
            )}
          </div>
        </aside>

        {/* [SIDEBAR-PREMIUM] Mobile Header */}
        <header className="mobile-header">
          <button 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-toggle"
          >
            <Menu size={24} />
          </button>
          
          <div className="mobile-brand">
            <div 
              className="mobile-avatar"
              style={{ background: boutiqueLogo ? 'transparent' : COLORS.accent }}
            >
              {boutiqueLogo ? (
                <img src={boutiqueLogo} alt={boutiqueName} />
              ) : (
                <span>{getInitials(boutiqueName)}</span>
              )}
            </div>
            <span className="mobile-name">{boutiqueName}</span>
          </div>
          
          {!isOnline && (
            <div className="mobile-offline">
              <WifiOff size={14} />
            </div>
          )}
        </header>

        {/* [SIDEBAR-PREMIUM] Mobile Overlay */}
        <div 
          className={`mobile-overlay ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* [SIDEBAR-PREMIUM] Mobile Sidebar */}
        <aside className={`mobile-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
          {/* Close button */}
          <button className="mobile-close" onClick={() => setMobileMenuOpen(false)}>
            <X size={24} />
          </button>
          
          {/* Header */}
          <div className="mobile-sidebar-header">
            <div 
              className="boutique-avatar"
              style={{ background: boutiqueLogo ? 'transparent' : `linear-gradient(135deg, ${COLORS.accent} 0%, #CC3700 100%)` }}
            >
              {boutiqueLogo ? (
                <img src={boutiqueLogo} alt={boutiqueName} />
              ) : (
                <span>{getInitials(boutiqueName)}</span>
              )}
            </div>
            <div className="boutique-info">
              <span className="boutique-name">{boutiqueName}</span>
              <span className="boutique-plan">
                {boutiquePlan.toUpperCase()}
              </span>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="mobile-nav">
            {filteredNavGroups.map((group) => (
              <div key={group.label} className="nav-group">
                <span className="nav-group-label">{group.label}</span>
                {group.items.map((item) => {
                  const badgeCount = item.badge ? getBadgeCount(item.badge) : 0;
                  const isActive = location.pathname.startsWith(item.path);
                  const locked = isPathLocked(item.path);
                  
                  if (locked) {
                    return (
                      <div
                        key={item.path}
                        className="nav-item locked"
                      >
                        <item.icon className="nav-icon locked" />
                        <span className="nav-label">{item.label}</span>
                        <Lock className="nav-lock-icon" size={14} />
                      </div>
                    );
                  }
                  
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={`nav-item ${isActive ? 'active' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {isActive && <div className="nav-active-indicator" />}
                      <item.icon className={`nav-icon ${isActive ? 'active' : ''}`} />
                      <span className="nav-label">{item.label}</span>
                      {badgeCount > 0 && (
                        <span className={`nav-badge ${item.badge === 'orders' ? 'orange' : 'red'}`}>
                          {badgeCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>
          
          {/* Profile */}
          <div className="mobile-profile">
            <div className="profile-avatar" style={{ background: roleInfo.gradient }}>
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div className="profile-info">
              <span className="profile-name">{displayName}</span>
              <span className="profile-role" style={{ color: roleInfo.color }}>{roleInfo.label}</span>
            </div>
            <button className="profile-logout" onClick={handleLogout}>
              <LogOut size={18} />
            </button>
          </div>
        </aside>

        {/* [SIDEBAR-PREMIUM] Bottom Navigation (Mobile) */}
        <nav className="bottom-nav">
          {filteredNavItems.slice(0, 4).map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            const badgeCount = item.badge ? getBadgeCount(item.badge) : 0;
            const locked = isPathLocked(item.path);
            
            if (locked) {
              return (
                <div
                  key={item.path}
                  className="bottom-nav-item locked"
                >
                  <div className="bottom-nav-icon-wrapper">
                    <Lock size={22} />
                  </div>
                  <span>{item.label.split(' ')[0]}</span>
                </div>
              );
            }
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`bottom-nav-item ${isActive ? 'active' : ''}`}
              >
                {isActive && <div className="bottom-nav-indicator" />}
                <div className="bottom-nav-icon-wrapper">
                  <item.icon size={22} className={isActive ? 'active' : ''} />
                  {badgeCount > 0 && <span className="bottom-nav-badge" />}
                </div>
                <span>{item.label.split(' ')[0]}</span>
              </NavLink>
            );
          })}
          <button 
            className="bottom-nav-item"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu size={22} />
            <span>Menu</span>
          </button>
        </nav>

        {/* [SIDEBAR-PREMIUM] Main Content */}
        <main className={`premium-main ${sidebarCollapsed ? 'expanded' : ''}`}>
          {/* [VALIDATION-FIX] Bannière persistante si compte non validé */}
          {!compteValide && (
            <div className="validation-banner" data-testid="validation-banner">
              <Clock size={16} className="validation-banner-icon" />
              <div className="validation-banner-text">
                <span className="validation-banner-primary">Votre compte est en cours de validation.</span>
                <span className="validation-banner-secondary">Accès complet activé après réception du paiement (485$) et validation par notre équipe.</span>
              </div>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Bonjour, je viens de créer ma boutique ${boutiqueData?.slug || ''} sur BoutiqueOS. Je souhaite finaliser mon paiement de 485$.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="validation-banner-btn"
                data-testid="validation-banner-contact"
              >
                Nous contacter →
              </a>
            </div>
          )}
          <div className="main-container">
            {isCurrentRouteLocked ? (
              <ValidationPendingScreen 
                boutiqueName={boutiqueName} 
                boutiqueSlug={boutiqueData?.slug}
              />
            ) : isRouteAuthorized ? (
              <Outlet context={{ isDark, toggleTheme, updateSidebarImage }} />
            ) : (
              <div className="unauthorized-view">
                <div className="unauthorized-icon">
                  <Lock size={40} />
                </div>
                <h1>Accès non autorisé</h1>
                <p>Vous n'avez pas les droits pour accéder à cette section.</p>
                <Button onClick={() => navigate(getDefaultRoute())} className="unauthorized-btn">
                  Retour
                </Button>
              </div>
            )}
          </div>
          {/* [THEME] Floating toggle button */}
          <button
            onClick={toggleTheme}
            className="theme-toggle-btn"
            data-testid="theme-toggle"
            title={isDark ? "Mode clair" : "Mode sombre"}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </main>
      </div>

      {/* [SIDEBAR-PREMIUM] Logout Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Voulez-vous vous déconnecter ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous serez redirigé vers la page de connexion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmLogout} className="bg-destructive hover:bg-destructive/90">
              Se déconnecter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
